'use client';

import { useMemo } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import type { OpenHouse } from '@/src/types/listingDetails.type';
import { ShowMoreContent } from '@/src/components/features/property/ui/ShowMoreContent';
import * as styles from './openHousesModule.styles';

type Props = {
  listing: PropertyDetailListing;
};

type UpcomingOpenHouse = OpenHouse & {
  sortKey: string;
};

const DATE_KEY_LENGTH = 10;

const getLocalDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isDateKey = (date: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(date);

const getOpenHouseSortKey = (openHouse: OpenHouse): string => {
  const starts = openHouse.starts || '00:00';
  return `${openHouse.date}T${starts}`;
};

export const getUpcomingOpenHouses = (
  openHouses: Array<OpenHouse> | null | undefined,
  todayKey: string = getLocalDateKey(new Date()),
): Array<UpcomingOpenHouse> =>
  (openHouses ?? [])
    .filter((openHouse) => isDateKey(openHouse.date) && openHouse.date >= todayKey)
    .map((openHouse) => ({
      ...openHouse,
      sortKey: getOpenHouseSortKey(openHouse),
    }))
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey));

const formatOpenHouseDate = (dateKey: string, locale: string): string => {
  if (!isDateKey(dateKey)) return dateKey;

  const normalizedDateKey = dateKey.slice(0, DATE_KEY_LENGTH);
  const year = Number(normalizedDateKey.slice(0, 4));
  const month = Number(normalizedDateKey.slice(5, 7));
  const day = Number(normalizedDateKey.slice(8, 10));
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  }).format(new Date(year, month - 1, day));
};

const formatOpenHouseTime = (time: string, locale: string): string => {
  const [hourPart = '', minutePart = ''] = time.split(':');
  const hour = Number(hourPart);
  const minute = Number(minutePart);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return time;

  return new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(2000, 0, 1, hour, minute));
};

const formatOpenHouseTimeRange = (openHouse: OpenHouse, locale: string): string => {
  const start = openHouse.starts ? formatOpenHouseTime(openHouse.starts, locale) : '';
  const end = openHouse.ends ? formatOpenHouseTime(openHouse.ends, locale) : '';
  if (start && end) return `${start} - ${end}`;
  return start || end;
};

export const OpenHousesModule = ({ listing }: Props) => {
  const t = useTranslations('hdp');
  const locale = useLocale();
  const upcomingOpenHouses = useMemo(() => getUpcomingOpenHouses(listing.openHouses), [listing.openHouses]);

  if (upcomingOpenHouses.length === 0) {
    return null;
  }

  return (
    <ShowMoreContent
      controlsId="open-houses-module-details"
      showMoreLabel={t('showMore')}
      showLessLabel={t('showLess')}
      showMoreAriaLabel={`${t('showMore')}: ${t('openHousesTitle')}`}
      showLessAriaLabel={`${t('showLess')}: ${t('openHousesTitle')}`}
    >
      <ul className={styles.list}>
        {upcomingOpenHouses.map((openHouse) => {
          const description = openHouse.description?.trim();
          const timeRange = formatOpenHouseTimeRange(openHouse, locale);

          return (
            <li key={openHouse.sortKey} className={styles.item}>
              <div className={styles.dateText}>{formatOpenHouseDate(openHouse.date, locale)}</div>
              {timeRange && <div className={styles.timeText}>{timeRange}</div>}
              {openHouse.appointmentRequired && <div className={styles.metaText}>{t('appointmentRequired')}</div>}
              {description && <p className={styles.description}>{description}</p>}
            </li>
          );
        })}
      </ul>
    </ShowMoreContent>
  );
};
